import React, { useState } from 'react';
import axios from 'axios';
import AppConst from '../apis/appconst';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const ChangePassword = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        current_password: '',  // ✅ ADDED
        new_password: '',
        confirm_password: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPasswords, setShowPasswords] = useState({
        current: false,  // ✅ ADDED
        new: false,
        confirm: false
    });

    const userData = JSON.parse(sessionStorage.getItem("ldata"));
    const userId = userData?.user_id;

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Toggle password visibility
    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        // ✅ VALIDATE CURRENT PASSWORD
        if (!formData.current_password) {
            newErrors.current_password = 'Current password is required';
        }

        if (!formData.new_password) {
            newErrors.new_password = 'New password is required';
        } else if (formData.new_password.length < 6) {
            newErrors.new_password = 'Password must be at least 6 characters';
        }

        // ✅ CHECK IF NEW PASSWORD IS SAME AS CURRENT
        if (formData.new_password && formData.current_password && 
            formData.new_password === formData.current_password) {
            newErrors.new_password = 'New password must be different from current password';
        }

        if (!formData.confirm_password) {
            newErrors.confirm_password = 'Please confirm your password';
        } else if (formData.new_password !== formData.confirm_password) {
            newErrors.confirm_password = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const params = new URLSearchParams();
            params.append('user_id', userId);
            params.append('current_password', formData.current_password);  // ✅ ADDED
            params.append('new_password', formData.new_password);

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.ChangePassword,
                params,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }
                }
            );

            console.log('Response:', response.data);

            if (response.data.status) {
                // Success SweetAlert
                Swal.fire({
                    title: 'Success!',
                    text: response.data.success || 'Password changed successfully!',
                    icon: 'success',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'OK'
                }).then(() => {
                    // Clear form after user clicks OK
                    setFormData({
                        current_password: '',  // ✅ ADDED
                        new_password: '',
                        confirm_password: ''
                    });
                });
            } else {
                // Error SweetAlert
                Swal.fire({
                    title: 'Error!',
                    text: response.data.success || 'Failed to change password',
                    icon: 'error',
                    confirmButtonColor: '#d33',
                    confirmButtonText: 'Try Again'
                });
            }
        } catch (error) {
            console.error('Error changing password:', error);
            console.error('Error response:', error.response);
            
            // Error SweetAlert
            Swal.fire({
                title: 'Error!',
                text: error.response?.data?.success || 'An error occurred. Please try again.',
                icon: 'error',
                confirmButtonColor: '#d33',
                confirmButtonText: 'Try Again'
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle cancel with confirmation
    const handleCancel = () => {
        // Check if form has any data
        const hasData = formData.current_password || formData.new_password || formData.confirm_password;  // ✅ UPDATED
        
        if (hasData) {
            Swal.fire({
                title: 'Are you sure?',
                text: "Your changes will be lost!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Yes, cancel',
                cancelButtonText: 'No, stay'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/');
                }
            });
        } else {
            navigate('/');
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0">Change Password</h4>
                        <div className="page-title-right">
                            <ol className="breadcrumb m-0">
                                <li className="breadcrumb-item"><a href="/">Home</a></li>
                                <li className="breadcrumb-item active">Change Password</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row justify-content-center">
                <div className="col-lg-6 col-md-8">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">
                                <i className="ri-lock-password-line me-2"></i>
                                Change Your Password
                            </h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                {/* ✅ CURRENT PASSWORD - NEW FIELD */}
                                <div className="mb-3">
                                    <label htmlFor="current_password" className="form-label">
                                        Current Password <span className="text-danger">*</span>
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type={showPasswords.current ? "text" : "password"}
                                            className={`form-control ${errors.current_password ? 'is-invalid' : ''}`}
                                            id="current_password"
                                            name="current_password"
                                            value={formData.current_password}
                                            onChange={handleChange}
                                            placeholder="Enter current password"
                                        />
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={() => togglePasswordVisibility('current')}
                                        >
                                            <i className={`ri-eye${showPasswords.current ? '-off' : ''}-line`}></i>
                                        </button>
                                        {errors.current_password && (
                                            <div className="invalid-feedback">
                                                {errors.current_password}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* New Password */}
                                <div className="mb-3">
                                    <label htmlFor="new_password" className="form-label">
                                        New Password <span className="text-danger">*</span>
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type={showPasswords.new ? "text" : "password"}
                                            className={`form-control ${errors.new_password ? 'is-invalid' : ''}`}
                                            id="new_password"
                                            name="new_password"
                                            value={formData.new_password}
                                            onChange={handleChange}
                                            placeholder="Enter new password"
                                        />
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={() => togglePasswordVisibility('new')}
                                        >
                                            <i className={`ri-eye${showPasswords.new ? '-off' : ''}-line`}></i>
                                        </button>
                                        {errors.new_password && (
                                            <div className="invalid-feedback">
                                                {errors.new_password}
                                            </div>
                                        )}
                                    </div>
                                    <small className="text-muted">Password must be at least 6 characters long</small>
                                </div>

                                {/* Confirm Password */}
                                <div className="mb-3">
                                    <label htmlFor="confirm_password" className="form-label">
                                        Confirm New Password <span className="text-danger">*</span>
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type={showPasswords.confirm ? "text" : "password"}
                                            className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                                            id="confirm_password"
                                            name="confirm_password"
                                            value={formData.confirm_password}
                                            onChange={handleChange}
                                            placeholder="Confirm new password"
                                        />
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={() => togglePasswordVisibility('confirm')}
                                        >
                                            <i className={`ri-eye${showPasswords.confirm ? '-off' : ''}-line`}></i>
                                        </button>
                                        {errors.confirm_password && (
                                            <div className="invalid-feedback">
                                                {errors.confirm_password}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="d-flex gap-2 justify-content-end">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleCancel}
                                        disabled={loading}
                                    >
                                        <i className="ri-close-line me-1"></i>
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Changing...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-line me-1"></i>
                                                Change Password
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Password Tips Card */}
                    <div className="card mt-3">
                        <div className="card-body">
                            <h6 className="card-title">
                                <i className="ri-information-line me-2"></i>
                                Password Tips
                            </h6>
                            <ul className="mb-0">
                                <li>Use at least 6 characters</li>
                                <li>Use a mix of letters, numbers, and symbols</li>
                                <li>Don't use common words or patterns</li>
                                <li>Don't reuse passwords from other accounts</li>
                                <li><strong>New password must be different from current password</strong></li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
